import axios from 'axios';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { apiBaseUrl } from '../../util/api';
import {
  TRANSITION_ZERO_CONFIRM_PAYMENT,
  TRANSITION_ZERO_DECLINE_PAYMENT,
} from '../../util/transaction';
import {
  fetchListingTransactions,
  loadData,
} from '../../containers/ApplicantShowingPage/ApplicantShowPage.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { checkCollision } from '../../util/workerTimeslotBlockChecker';
import moment, { utc } from 'moment';

// ================ Action types ================ //

export const ACCEPT_APPLIED_JOB_REQUEST = 'app/BusinessDashboard/ACCEPT_APPLIED_JOB_REQUEST';
export const ACCEPT_APPLIED_JOB_SUCCESS = 'app/BusinessDashboard/ACCEPT_APPLIED_JOB_SUCCESS';
export const ACCEPT_APPLIED_JOB_ERROR = 'app/BusinessDashboard/ACCEPT_APPLIED_JOB_ERROR';

export const DECLINE_APPLIED_JOB_REQUEST = 'app/BusinessDashboard/DECLINE_APPLIED_JOB_REQUEST';
export const DECLINE_APPLIED_JOB_SUCCESS = 'app/BusinessDashboard/DECLINE_APPLIED_JOB_SUCCESS';
export const DECLINE_APPLIED_JOB_ERROR = 'app/BusinessDashboard/DECLINE_APPLIED_JOB_ERROR';

export const SET_WORKER_TIMESLOT_BLOCKED_STATUS =
  'app/BusinessDashboard/SET_WORKER_TIMESLOT_BLOCKED_STATUS';

export const SET_LISTING_VACANCY_FULL_STATUS =
  'app/BusinessDashboard/SET_LISTING_VACANCY_FULL_STATUS';

// ================ Reducer ================ //

const initialState = {
  acceptAppliedJobInProgress: [],
  acceptAppliedJobSuccess: false,
  acceptAppliedJobError: null,
  isWorkerTimeslotBlocked: false,
  isListingVacancyFull: false,
  declineAppliedJobInProgress: [],
  declineAppliedJobSuccess: false,
  declineAppliedJobError: null,
};

export default function WorkerCardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case ACCEPT_APPLIED_JOB_REQUEST:
      return {
        ...state,
        acceptAppliedJobInProgress: Array.isArray(state.acceptAppliedJobInProgress)
          ? [...state.acceptAppliedJobInProgress, payload]
          : [payload],
        acceptAppliedJobSuccess: false,
      };
    case ACCEPT_APPLIED_JOB_SUCCESS: {
      return {
        ...state,
        acceptAppliedJobInProgress: [],
        acceptAppliedJobSuccess: true,
      };
    }
    case ACCEPT_APPLIED_JOB_ERROR:
      // console.error(payload); // eslint-disable-line
      return { ...state, acceptAppliedJobInProgress: [], acceptAppliedJobError: payload };

    case DECLINE_APPLIED_JOB_REQUEST:
      return {
        ...state,
        declineAppliedJobInProgress: Array.isArray(state.declineAppliedJobInProgress)
          ? [...state.declineAppliedJobInProgress, payload]
          : [payload],
        declineAppliedJobSuccess: false,
      };
    case DECLINE_APPLIED_JOB_SUCCESS:
      return {
        ...state,
        declineAppliedJobInProgress: [],
        declineAppliedJobSuccess: true,
      };
    case DECLINE_APPLIED_JOB_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, declineAppliedJobInProgress: [], declineAppliedJobError: payload };

    case SET_LISTING_VACANCY_FULL_STATUS:
      return { ...state, isListingVacancyFull: payload };

    case SET_WORKER_TIMESLOT_BLOCKED_STATUS:
      return { ...state, isWorkerTimeslotBlocked: payload };

    default:
      return state;
  }
}
// ================ Action creators ================ //

const acceptAppliedJobRequest = payload => ({ type: ACCEPT_APPLIED_JOB_REQUEST, payload });
const acceptAppliedJobSuccess = response => ({
  type: ACCEPT_APPLIED_JOB_SUCCESS,
  payload: response,
});
const acceptAppliedJobError = e => ({
  type: ACCEPT_APPLIED_JOB_ERROR,
  error: true,
  payload: e,
});

const declineAppliedJobRequest = payload => ({ type: DECLINE_APPLIED_JOB_REQUEST, payload });
const declineAppliedJobSuccess = response => ({
  type: DECLINE_APPLIED_JOB_SUCCESS,
  payload: response,
});
const declineAppliedJobError = e => ({
  type: DECLINE_APPLIED_JOB_ERROR,
  error: true,
  payload: e,
});

export const setWorkerBlockedTimeslotStatus = payload => ({
  type: SET_WORKER_TIMESLOT_BLOCKED_STATUS,
  payload: !!payload,
});

export const setListingVacancyFullStatus = payload => ({
  type: SET_LISTING_VACANCY_FULL_STATUS,
  payload: !!payload,
});

// ================ Thunks ================ //

export const acceptAppliedJob = (txId, listingId, workerDetails, listingTimeslots) => (
  dispatch,
  getState,
  sdk
) => {
  const workerId = workerDetails.id.uuid;
  dispatch(acceptAppliedJobRequest(workerId));
  dispatch(setWorkerBlockedTimeslotStatus(false));
  dispatch(setListingVacancyFullStatus(false));
  let apiResponse;
  return axios
    .post(`${apiBaseUrl()}/api/timeStatement/getBlockedTimes`, {
      workerId,
    })
    .then(resp => {
      const workerBlockedTimeslots = resp.data;
      const isBlocked = checkCollision(listingTimeslots, workerBlockedTimeslots);

      if (isBlocked) {
        const error = new Error();
        error.response = { data: { isWorkerBlocked: true } };
        throw error;
      }
      const gigVacancyStatusUrl = `${apiBaseUrl()}/api/gig-vacancy-status/${listingId}`;
      return axios.patch(gigVacancyStatusUrl, {});
    })
    .then(() =>
      sdk.transactions.transition(
        { id: txId, transition: TRANSITION_ZERO_CONFIRM_PAYMENT, params: {} },
        { expand: true }
      )
    )
    .then(response => {
      apiResponse = response;
      const url = `${apiBaseUrl()}/api/TimeStatement/accept/${txId}`;
      return axios.patch(url);
    })
    .then(() => {
      let params;
      if (workerDetails?.attributes?.profile?.publicData?.appliedListingId?.includes(listingId)) {
        params = {
          appliedListingId: workerDetails?.attributes?.profile?.publicData?.appliedListingId?.filter(
            f => f !== listingId
          ),
        };
      } else {
        params = {
          appliedListingId: workerDetails?.attributes?.profile?.publicData?.appliedListingId
            ? [...workerDetails?.attributes?.profile?.publicData?.appliedListingId, listingId]
            : [listingId],
        };
      }
      const profile = {
        id: workerDetails.id.uuid,
        publicData: { ...params },
      };

      const updatedValue = { profile, acceptedListing: listingId };
      return axios.post(`${apiBaseUrl()}/api/updateWorker`, updatedValue);
    })
    .then(() => {
      dispatch(acceptAppliedJobSuccess(apiResponse));
      dispatch(
        // fetchListingTransactions(listingId, _, _, [
        //   'transition/decline-zero-payment',
        //   'transition/confirm-zero-payment',
        // ])
        // fetchListingTransactions(listingId)
        loadData({ id: listingId })
      );
      return denormalisedResponseEntities(apiResponse)[0];
    })
    .catch(error => {
      const canApply = error?.response?.data?.canApply;
      const isWorkerBlocked = error?.response?.data?.isWorkerBlocked;
      if (typeof isWorkerBlocked === 'boolean' && isWorkerBlocked) {
        dispatch(setWorkerBlockedTimeslotStatus(true));
      }
      if (typeof canApply === 'boolean' && !canApply) {
        dispatch(setListingVacancyFullStatus(true));
      }
      dispatch(acceptAppliedJobError(error));
      throw error;
    });
};

export const declineAppliedJob = (txId, listingId, workerDetails) => (dispatch, getState, sdk) => {
  dispatch(declineAppliedJobRequest(workerDetails.id.uuid));
  dispatch(setWorkerBlockedTimeslotStatus(false));
  dispatch(setListingVacancyFullStatus(false));
  let apiResponse;
  return sdk.transactions
    .transition(
      { id: txId, transition: TRANSITION_ZERO_DECLINE_PAYMENT, params: {} },
      { expand: true }
    )
    .then(response => {
      apiResponse = response;
      const url = `${apiBaseUrl()}/api/TimeStatement/decline/${txId}`;
      return axios.patch(url);
    })
    .then(() => {
      let params;
      if (workerDetails?.attributes?.profile?.publicData?.appliedListingId?.includes(listingId)) {
        params = {
          appliedListingId: workerDetails?.attributes?.profile?.publicData?.appliedListingId?.filter(
            f => f !== listingId
          ),
        };
      }
      const profile = {
        id: workerDetails.id.uuid,
        publicData: { ...params },
      };

      const updatedValue = { profile, declinedListing: listingId };
      return axios.post(`${apiBaseUrl()}/api/updateWorker`, updatedValue);
    })
    .then(() => {
      dispatch(declineAppliedJobSuccess(apiResponse));
      dispatch(
        // fetchListingTransactions(listingId, _, _, [
        //   'transition/decline-zero-payment',
        //   'transition/confirm-zero-payment',
        // ])
        // fetchListingTransactions(listingId)
        loadData({ id: listingId })
      );
      return denormalisedResponseEntities(apiResponse)[0];
    })

    .catch(error => {
      dispatch(declineAppliedJobError(error));
      throw error;
    });
};
